
import { Badge } from '@repo/ui/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { imageService } from '@repo/ui/lib/services/image.service';
import { Camera, Leaf, MapPin, Ruler, TreePine } from 'lucide-react';

interface IdentificationDetailsProps {
  formData: { [key: string]: any };
  originalData: any | null;
}

export function IdentificationDetails({ formData, originalData }: IdentificationDetailsProps) {
  const speciesData = [
    {
      label: "Genre",
      value: formData['_espece']?.genre || 'Non renseigné',
      icon: <Leaf className="w-4 h-4" />
    },
    {
      label: "Espèce",
      value: formData['_espece']?.name || 'Non renseigné',
      icon: <TreePine className="w-4 h-4" />
    },
    {
      label: "Nom français",
      value: formData['_espece']?.nom_fr || 'Non renseigné',
      icon: <Leaf className="w-4 h-4" />
    }
  ];

  const measurementData = [
    {
      label: "Code site",
      value: formData['code_site'] || 'Non renseigné',
      unit: null
    },
    {
      label: "Hauteur",
      value: formData['hauteur'] || '—',
      unit: "m"
    },
    {
      label: "Diamètre",
      value: formData['diametre'] || '—',
      unit: "cm"
    }
  ];

  const especeImgUrl = formData['_espece']?.img_url
    ? imageService.getPrimaryImage(formData['_espece'].img_url, 'espece')
    : null;

  const images = [
    formData['img1'] || (originalData as any)?.data?.arbre?.imgUrl?.img1 || especeImgUrl,
    formData['img2'] || (originalData as any)?.data?.arbre?.imgUrl?.img2,
    formData['img3'] || (originalData as any)?.data?.arbre?.imgUrl?.img3,
  ];

  return (
    <div className="space-y-8">
      {/* Species Information Card */}
      <Card className="border-border/50 bg-card shadow-sm ring-1 ring-border/20 dark:border-border dark:ring-border/30">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl font-semibold text-foreground">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
              <TreePine className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            Informations sur l'espèce
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {speciesData.map((item, index) => (
              <div
                key={index}
                className="group rounded-xl border border-border/50 bg-gradient-to-br from-card to-primary/5 p-4 transition-all hover:border-border hover:shadow-sm dark:border-border dark:to-primary/10 dark:hover:shadow-md"
              >
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
                    {item.icon}
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {item.label}
                  </span>
                </div>
                <Badge
                  variant="secondary"
                  className="w-full justify-center border-border/50 bg-muted/50 py-2.5 px-4 text-sm font-semibold text-foreground shadow-sm dark:border-border dark:bg-muted/30"
                >
                  {item.value}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Measurements and Location Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Measurements Card */}
        <Card className="border-border/50 bg-card shadow-sm ring-1 ring-border/20 dark:border-border dark:ring-border/30">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-xl font-semibold text-foreground">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
                <Ruler className="h-5 w-5 text-primary" aria-hidden="true" />
              </div>
              Mesures et identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {measurementData.map((item, index) => (
                <div
                  key={index}
                  className="group flex items-center justify-between rounded-xl border border-border/50 bg-gradient-to-br from-card to-primary/5 p-4 transition-all hover:border-border hover:shadow-sm dark:border-border dark:to-primary/10 dark:hover:shadow-md"
                >
                  <span className="text-sm font-semibold text-foreground">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-foreground">{item.value}</span>
                    {item.unit && (
                      <Badge
                        variant="outline"
                        className="border-primary/30 bg-primary/5 px-2 text-xs font-semibold text-primary dark:border-primary/40 dark:bg-primary/10"
                      >
                        {item.unit}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Location Card */}
        <Card className="border-border/50 bg-card shadow-sm ring-1 ring-border/20 dark:border-border dark:ring-border/30">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3 text-xl font-semibold text-foreground">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
                <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
              </div>
              Localisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="rounded-xl border border-border/50 bg-gradient-to-br from-card to-primary/5 p-4 transition-all hover:border-border hover:shadow-sm dark:border-border dark:to-primary/10 dark:hover:shadow-md">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Adresse
                </div>
                <div className="text-base font-semibold text-foreground">
                  {formData['_address'] || originalData?.data?.arbre?.address || 'Non renseignée'}
                </div>
              </div>
              <div className="rounded-xl border border-border/50 bg-gradient-to-br from-card to-primary/5 p-4 transition-all hover:border-border hover:shadow-sm dark:border-border dark:to-primary/10 dark:hover:shadow-md">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Ville
                </div>
                <div className="text-base font-semibold text-foreground">
                  {formData['_ville'] || originalData?.data?.arbre?.ville || 'Non renseignée'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Images Card */}
      <Card className="border-border/50 bg-card shadow-sm ring-1 ring-border/20 dark:border-border dark:ring-border/30">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl font-semibold text-foreground">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/20 dark:ring-primary/30">
              <Camera className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            Galerie d'images
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {images.map((src, idx) => (
              <div key={idx} className="group relative">
                <div className="relative overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm ring-1 ring-border/20 transition-all duration-200 group-hover:border-border group-hover:shadow-md dark:border-border dark:ring-border/30 dark:group-hover:shadow-lg">
                  {src ? (
                    <>
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={imageService.addCacheBusting(imageService.getImageUrl(src, "arbre") as string)}
                        alt={`Image ${idx + 1}`}
                        className="h-56 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute bottom-3 left-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        <Badge
                          variant="secondary"
                          className="border-border/50 bg-background/95 px-3 py-1 text-xs font-semibold text-foreground shadow-md backdrop-blur-sm dark:border-border dark:bg-background/95"
                        >
                          Image {idx + 1}
                        </Badge>
                      </div>
                    </>
                  ) : (
                    <div className="flex h-56 flex-col items-center justify-center border-2 border-dashed border-border/50 bg-muted/30 dark:border-border dark:bg-muted/20">
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted ring-1 ring-border/20 dark:ring-border/30">
                        <Camera className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                      </div>
                      <span className="text-sm font-medium text-muted-foreground">Aucune image</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}